import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";

import { cn } from "@wellcheck/components/lib/cn";

/**
 * shadcn calendar (react-day-picker v9 ベース) を wellcheck 用に薄くラップ。
 * 旧 v8 と DOM 構造が変わっているので classNames キーは v9 のものに合わせる。
 *
 * - month_caption は flex で中央寄せ、左右の nav ボタン (button_previous/_next) は
 *   month_caption の絶対配置にせず、month の上に独立した nav 要素として置く構造に従う。
 * - range 中央は `range_middle`、端は `range_start` / `range_end` で当てる。
 *   セル全体ではなく day_button の背景色で塗り、左右端の角丸も day_button に当てる。
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("relative p-3", className)}
      classNames={{
        ...defaultClassNames,
        months: cn(defaultClassNames.months, "relative flex flex-col gap-4 sm:flex-row"),
        month: cn(defaultClassNames.month, "flex w-full flex-col gap-4"),

        // nav は月キャプションの左右ではなく、Calendar 全体の上端に固定
        nav: cn(
          defaultClassNames.nav,
          "absolute inset-x-1 top-1 flex items-center justify-between",
        ),
        button_previous: cn(
          defaultClassNames.button_previous,
          "inline-flex size-7 items-center justify-center rounded-md border border-border bg-transparent p-0 text-foreground/70 hover:bg-accent hover:text-accent-foreground",
        ),
        button_next: cn(
          defaultClassNames.button_next,
          "inline-flex size-7 items-center justify-center rounded-md border border-border bg-transparent p-0 text-foreground/70 hover:bg-accent hover:text-accent-foreground",
        ),

        month_caption: cn(defaultClassNames.month_caption, "flex h-7 items-center justify-center"),
        caption_label: cn(defaultClassNames.caption_label, "text-sm font-medium"),

        month_grid: cn(defaultClassNames.month_grid, "w-full border-collapse"),
        weekdays: cn(defaultClassNames.weekdays, "flex"),
        weekday: cn(
          defaultClassNames.weekday,
          "w-9 text-center text-[0.75rem] font-normal text-muted-foreground",
        ),
        week: cn(defaultClassNames.week, "mt-1 flex w-full"),

        // 各セル (td 相当)。背景は当てず day_button 側で塗る
        day: cn(defaultClassNames.day, "relative size-9 p-0 text-center text-sm"),
        day_button: cn(
          defaultClassNames.day_button,
          "size-9 rounded-md p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground",
        ),

        // range 表示。中央は accent (薄い灰)、端は primary (青)。
        // range_middle の背景は day_button にも乗るので角丸を消す
        range_start: cn(
          defaultClassNames.range_start,
          "[&_button]:bg-primary [&_button]:text-primary-foreground [&_button]:hover:bg-primary [&_button]:hover:text-primary-foreground",
        ),
        range_end: cn(
          defaultClassNames.range_end,
          "[&_button]:bg-primary [&_button]:text-primary-foreground [&_button]:hover:bg-primary [&_button]:hover:text-primary-foreground",
        ),
        range_middle: cn(
          defaultClassNames.range_middle,
          "[&_button]:rounded-none [&_button]:bg-accent [&_button]:text-accent-foreground [&_button]:hover:bg-accent [&_button]:hover:text-accent-foreground",
        ),

        selected: cn(defaultClassNames.selected),
        today: cn(
          defaultClassNames.today,
          "[&_button]:font-semibold [&_button]:text-foreground [&_button]:underline",
        ),
        outside: cn(defaultClassNames.outside, "text-muted-foreground/50"),
        disabled: cn(defaultClassNames.disabled, "text-muted-foreground/40"),
        hidden: cn(defaultClassNames.hidden, "invisible"),
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, ...rest }) => {
          if (orientation === "left") return <ChevronLeft className="size-4" {...rest} />;
          return <ChevronRight className="size-4" {...rest} />;
        },
      }}
      {...props}
    />
  );
}

export { Calendar };
